import { useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { usePasses } from "../context/PassesContext";
import { useSettings } from "../context/SettingsContext";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";
import ReceiptView from "../components/ReceiptView";

export default function PassPrint() {
  const { passId } = useParams();
  const { getPass } = usePasses();
  const { loading: settingsLoading } = useSettings();
  const [pass, setPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const printedRef = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    document.title = `Late Pass ${passId} — Ohr HaTorah`;
    let cancelled = false;
    (async () => {
      try {
        const p = await getPass(passId);
        if (cancelled) return;
        if (p) {
          setPass(p);
        } else {
          setNotFound(true);
        }
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [passId, getPass]);

  // Trigger print AFTER pass loaded AND settings loaded AND images ready
  useEffect(() => {
    if (!pass || settingsLoading || printedRef.current) return;

    const doPrint = () => {
      if (printedRef.current) return;
      printedRef.current = true;
      // small delay so browser has rendered
      setTimeout(() => {
        window.print();
        // Auto-close the print tab shortly after so the user returns to the
        // form to make another pass. window.close() only works for tabs
        // opened via window.open() (which is the case here).
        setTimeout(() => {
          try { window.close(); } catch { /* ignore */ }
        }, 600);
      }, 200);
    };

    // Wait for all images inside the container to finish loading
    const container = containerRef.current;
    if (!container) {
      doPrint();
      return;
    }
    const imgs = container.querySelectorAll("img");
    if (imgs.length === 0) {
      doPrint();
      return;
    }
    let loaded = 0;
    const check = () => { loaded += 1; if (loaded >= imgs.length) doPrint(); };
    imgs.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        check();
      } else {
        img.addEventListener("load", check, { once: true });
        img.addEventListener("error", check, { once: true });
      }
    });
    // Fallback timeout in case images never fire load
    const fallback = setTimeout(doPrint, 2500);
    return () => clearTimeout(fallback);
  }, [pass, settingsLoading]);

  if (loading || settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
          <div className="text-sm text-slate-500 mt-3">Loading pass…</div>
        </div>
      </div>
    );
  }

  if (notFound || !pass) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="bg-white p-8 rounded-lg shadow max-w-md text-center">
          <h2 className="text-xl font-bold mb-2">Pass not found</h2>
          <p className="text-slate-500 mb-4">Pass ID: {passId}</p>
          <Button onClick={() => window.close()}>Close</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-4">
      <style>{`
        @page { size: 80mm auto; margin: 0; }
        @media print {
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print { display: none !important; }
          .receipt {
            box-shadow: none !important;
            margin: 0 !important;
            width: 80mm !important;
            max-width: 80mm !important;
            border-radius: 0 !important;
            padding: 3mm !important;
            color: #000 !important;
          }
          .receipt * { color: #000 !important; }
        }
      `}</style>

      <div className="no-print max-w-sm mx-auto px-4 mb-4 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => window.close()}>
          <ArrowLeft className="w-4 h-4 mr-2" />Close
        </Button>
        <Button size="sm" onClick={() => window.print()} className="gradient-blue-mild text-white">
          <Printer className="w-4 h-4 mr-2" />Print
        </Button>
      </div>

      <div ref={containerRef} className="mx-auto" style={{ width: "80mm" }}>
        <ReceiptView pass={pass} />
      </div>

      <div className="no-print text-center text-xs text-slate-500 mt-4 max-w-sm mx-auto px-4">
        <div>Optimized for the Milestone MHT-P80E thermal printer (80mm).</div>
        <div className="mt-1">If the print dialog didn't open, click the Print button above.</div>
      </div>
    </div>
  );
}
